import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { Activity } from '@go-modules/interfaces/activity';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { Subject, takeUntil } from 'rxjs';
import { PrivacyOption, PrivacyOptionKey } from './react-task-additional-settings.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { selectedServiceToken } from 'go-modules/services/selected/selected.service';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';

const PRIVACY_OPTIONS: PrivacyOption[] = [
	{
		key: 'PRIVATE',
		titleKey: 'activity-editor-new_private',
		descriptionKey: 'activity-editor-new_private-description',
		peerReview: false,
		publicFeedback: false
	},
	{
		key: 'CLOSED_PEER_REVIEW',
		titleKey: 'activity-editor-new_closed-peer-review',
		descriptionKey: 'activity-editor-new_closed-peer-review-description',
		peerReview: true,
		publicFeedback: false
	},
	{
		key: 'EVERYONE_SEES_EVERYTHING',
		titleKey: 'activity-editor-new_everyone-sees-everything',
		descriptionKey: 'activity-editor-new_everyone-sees-everything-description',
		peerReview: true,
		publicFeedback: true
	}
];

@Component({
	selector: 'react-task-additional-settings',
	templateUrl: './react-task-additional-settings.component.html',
	styleUrls: ['./react-task-additional-settings.component.scss'],
	standalone: true,
	imports: [
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatSelectModule,
		MatIconModule
	]
})
export class ReactTaskAdditionalSettingsComponent implements OnInit, OnDestroy {
	public activity: ActivityModel;
	public privacyOptions = PRIVACY_OPTIONS;
	public privacyControl = new FormControl<PrivacyOptionKey | null>(null);

	private destroy$ = new Subject<void>();
	private activityEditModeService = inject(ActivityEditModeService);
	private activityModelFactory = inject(activityToken);
	private selectedService = inject(selectedServiceToken);
	private editorContext: ActivityEditorContext | null = inject(ACTIVITY_EDITOR_CONTEXT, {optional: true});

	public ngOnInit (): void {
		this.initializeForm();

		this.privacyControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (value == null) {
					return;
				}
				const option = this.privacyOptions.find((item) => item.key === value);
				if (option == null) {
					return;
				}
				this.applyActivityChanges({
					peer_review: option.peerReview,
					public_feedback: option.publicFeedback
				});
			});

		if (this.editorContext == null) {
			this.activityEditModeService.changesReverted$
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					const rawActivity = this.selectedService.getActivity();
					if (rawActivity) {
						this.initializeForm(rawActivity);
					}
				});
		}
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public getSelectedPrivacyOption (): PrivacyOption | undefined {
		return this.privacyOptions.find((option) => option.key === this.privacyControl.value);
	}

	public shouldShowPrivacy (): boolean {
		return this.activity && this.activity.isViewable('peer_review');
	}

	private initializeForm (activityOverride?: Activity): void {
		const workingActivity = activityOverride ?? this.getCurrentActivity();
		if (workingActivity == null) {
			return;
		}

		this.activity = this.activityModelFactory.model(workingActivity);

		const optionKey = this.getOptionKeyForActivity(workingActivity);
		this.privacyControl.setValue(optionKey, {emitEvent: false});
	}

	private applyActivityChanges (changes: Partial<Activity>): void {
		const current = this.getCurrentActivity();
		if (current == null) {
			return;
		}

		const updatedActivity = this.syncParticipantRubricCritiqueSettings({
			...current,
			...changes
		} as Activity);

		this.activity = this.activityModelFactory.model(updatedActivity);
		if (this.editorContext != null) {
			this.editorContext.updateActivity(updatedActivity);
			return;
		}

		this.activityEditModeService.updateEditedActivity(updatedActivity);
	}

	private syncParticipantRubricCritiqueSettings (activity: Activity): Activity {
		if (this.editorContext == null || activity.peer_review == null) {
			return activity;
		}

		const hasParticipantRubric = (activity.peer_rubric_id ?? 0) > 0;
		return {
			...activity,
			peer_rubric_critique_enabled: hasParticipantRubric && activity.peer_review,
			self_rubric_critique_enabled: hasParticipantRubric
		};
	}

	private getOptionKeyForActivity (activity: Activity): PrivacyOptionKey {
		const found = this.privacyOptions.find((option) =>
			option.peerReview === !!activity.peer_review &&
			option.publicFeedback === !!activity.public_feedback
		);

		return (found?.key ?? PRIVACY_OPTIONS[1].key) as PrivacyOptionKey;
	}

	private getCurrentActivity (): Activity | null {
		if (this.editorContext != null) {
			return this.editorContext.activity();
		}

		return this.activityEditModeService.currentActivity();
	}
}
